/**
 * What a member sees when their organization has nothing they can open.
 *
 * This used to be `DocsSurfaceError kind="no-access"` reading "This
 * organization has no project available for Shared Docs" — an error panel for
 * a state that is not an error. It is the ordinary condition of a brand-new
 * teammate whose inviter has not shared anything yet, and it is reachable
 * whenever an admin sends a bare invitation. Now that an accepted invitation
 * lands here instead of on a desktop handoff card, it is also the first
 * Nimbalyst surface some people ever see, so it has to carry a next action
 * rather than end the session.
 *
 * The next action is "ask someone who can share". Note it is deliberately not
 * "ask the person who invited you": Stytch is told `invited_by_member_id` when
 * the invitation is sent (`collabv3/src/teams.ts`), but nothing on our side
 * persists it, so the console cannot know who that was. Naming the org's
 * owners and admins is the true version of the same action — they are exactly
 * the people who can act on the request.
 */

import { useEffect, useState } from 'react';
import { listMembers, type TeamMember } from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import { DesktopAppOffer } from './DesktopAppOffer';
import { PanelState } from './Chrome';

/** Roles that can actually share something into a project. */
const CAN_SHARE = new Set(['owner', 'admin']);

/**
 * Who to ask, in the order a reader should try them. Owners first: on a small
 * team the owner is usually the person who invited you, and on a large one they
 * are the least ambiguous choice.
 */
export function membersWorthAsking(members: readonly TeamMember[]): TeamMember[] {
  return members
    .filter((member) => CAN_SHARE.has(member.role) && member.status !== 'invited')
    .sort((a, b) => (a.role === b.role ? 0 : a.role === 'owner' ? -1 : 1))
    .slice(0, 3);
}

/** The roster lookup, kept out of the component so the branch can be tested. */
function useMembersWorthAsking(
  orgId: string,
  getTeamJwt: (() => Promise<TeamJwt>) | null,
): TeamMember[] {
  const [asking, setAsking] = useState<TeamMember[]>([]);
  useEffect(() => {
    if (!getTeamJwt) return;
    let active = true;
    void (async () => {
      try {
        const { members } = await listMembers(await getTeamJwt(), orgId);
        if (active) setAsking(membersWorthAsking(members));
      } catch (cause) {
        // Never a gate. The panel's copy and its desktop offer stand on their
        // own; the roster only lets it name names.
        console.warn('[docs] could not list who to ask about sharing', cause);
      }
    })();
    return () => { active = false; };
  }, [getTeamJwt, orgId]);
  return asking;
}

export function NothingSharedYet({
  orgName,
  orgId,
  getTeamJwt,
}: {
  orgName: string;
  orgId: string;
  /** Null when no team session has resolved; the panel still renders. */
  getTeamJwt?: (() => Promise<TeamJwt>) | null;
}) {
  const asking = useMembersWorthAsking(orgId, getTeamJwt ?? null);
  return (
    <PanelState
      tone="empty"
      className="nothing-shared-yet w-full max-w-[34rem] gap-[0.9rem] text-center"
      data-docs-state="nothing-shared-yet"
    >
      <strong className="text-[1.05rem]">Your team hasn&rsquo;t shared anything with you yet</strong>
      <span>
        You&rsquo;re a member of {orgName}. This is normal right after joining — someone with access
        has to share a folder or a document before it shows up here, and it will appear the moment
        they do.
      </span>

      {asking.length > 0 && (
        <span className="ask-to-share text-nim-muted">
          {asking.length === 1 ? 'Ask ' : 'Ask one of '}
          {asking.map((member, index) => (
            <span key={member.memberId}>
              {index > 0 && (index === asking.length - 1 ? ' or ' : ', ')}
              <a className="font-medium text-[var(--nim-primary)] no-underline hover:underline" href={`mailto:${member.email}`}>
                {member.name || member.email}
              </a>
            </span>
          ))}
          {' to share a folder with you.'}
        </span>
      )}

      <DesktopAppOffer
        className="mt-[0.4rem] text-left"
        lead="Or start your own work in the desktop app, and share it back here."
        placement="empty_org"
        orgId={orgId}
      />
    </PanelState>
  );
}
